import { CategoryAxis, ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { type LegendKey, SvgLegend } from "@/components/tool-kit/charts/legend";
import { Marker, px } from "@/components/tool-kit/charts/marks";
import type { BoxStats } from "@/lib/tool-kit/charts/box";
import { colorIndex, paletteColor } from "@/lib/tool-kit/charts/color";
import {
  type NumberFormat,
  tickFormat,
  valueText,
} from "@/lib/tool-kit/charts/format";
import {
  type CategoryLabelLayout,
  CHART_FONT_SIZE,
  CHART_HEIGHT,
  CHART_PADDING,
  CHART_WIDTH,
  cartesianFrame,
  type Frame,
  type FrameInput,
  heightForLabels,
  heightForPlot,
  layoutCategoryLabels,
  layoutLegend,
  rotatedOverflow,
} from "@/lib/tool-kit/charts/layout";
import {
  bandScale,
  linearScale,
  niceTicks,
  type Scale,
} from "@/lib/tool-kit/charts/scale";
import { maxTextWidth, wrapText } from "@/lib/tool-kit/charts/text";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * Box plots, one per group, from `boxStats`: the box runs Q1 to Q3 with a line at the
 * median, the whiskers reach the most extreme values inside Tukey's fences, and every
 * value beyond a fence is drawn on its own as a hollow circle. A legend inside the
 * image names the symbols, as APA asks of a figure's symbols.
 */

export interface BoxGroup {
  name: string;
  stats: BoxStats;
}

export interface BoxLegendLabels {
  legendMean: string;
  legendOutlier: string;
}

type FrameBase = Omit<
  FrameInput,
  "height" | "leftLabelWidth" | "bottomLabelHeight"
>;

const TICK_STYLE = { fontSize: CHART_FONT_SIZE.tick };
const BAND = { paddingInner: 0.35, paddingOuter: 0.2 };

/** Where along the band axis a box sits, and how a value maps across it. */
interface BoxGeometry {
  frame: Frame;
  labels: CategoryLabelLayout;
  center: (index: number) => number;
  thickness: number;
  value: Scale;
}

function boxGeometry(
  groups: BoxGroup[],
  horizontal: boolean,
  base: FrameBase,
  axis: { min: number; max: number; ticks: number[] },
  tick: (value: number) => string
): BoxGeometry {
  if (horizontal) {
    const lines = groups.map((g) =>
      wrapText(g.name, Math.min(180, CHART_WIDTH * 0.28), TICK_STYLE, 2)
    );
    const input = {
      ...base,
      leftLabelWidth: maxTextWidth(lines.flat(), TICK_STYLE),
      bottomLabelHeight: CHART_FONT_SIZE.tick,
    };
    const frame = cartesianFrame({
      ...input,
      height: heightForPlot(Math.max(140, groups.length * 64), input),
    });
    const { plot } = frame;
    const band = bandScale(groups.length, [plot.y, plot.y + plot.height], BAND);
    return {
      frame,
      labels: { rotated: false, lines, height: 0 },
      center: band.center,
      thickness: Math.min(band.bandwidth, 44),
      value: linearScale([axis.min, axis.max], [plot.x, plot.x + plot.width]),
    };
  }
  const input = {
    ...base,
    height: CHART_HEIGHT,
    leftLabelWidth: maxTextWidth(axis.ticks.map(tick), TICK_STYLE),
  };
  const probe = cartesianFrame({ ...input, bottomLabelHeight: 0 });
  const step = bandScale(groups.length, [0, probe.plot.width], BAND).step;
  const labels = layoutCategoryLabels(
    groups.map((g) => g.name),
    step
  );
  const sized = {
    ...input,
    height: heightForLabels(labels.height),
    bottomLabelHeight: labels.height,
  };
  const firstCenter = step * (BAND.paddingOuter + (1 - BAND.paddingInner) / 2);
  const overflow = rotatedOverflow(cartesianFrame(sized), labels, firstCenter);
  const frame = cartesianFrame({
    ...sized,
    leftLabelWidth: sized.leftLabelWidth + overflow,
  });
  const { plot } = frame;
  const band = bandScale(groups.length, [plot.x, plot.x + plot.width], BAND);
  return {
    frame,
    labels,
    center: band.center,
    thickness: Math.min(band.bandwidth, 56),
    value: linearScale([axis.min, axis.max], [plot.y + plot.height, plot.y]),
  };
}

/** One box with its whiskers, outliers, mean and median label. */
function Box({
  stats,
  center,
  thickness,
  value,
  horizontal,
  color,
  showMean,
  medianLabel,
}: {
  stats: BoxStats;
  center: number;
  thickness: number;
  value: Scale;
  horizontal: boolean;
  color: string;
  showMean: boolean;
  medianLabel: string | null;
}) {
  const half = thickness / 2;
  const cap = thickness / 4;
  // Draw in (along, across) coordinates and swap for a vertical plot.
  const at = (along: number, across: number): [number, number] =>
    horizontal ? [along, across] : [across, along];
  const segment = (
    a1: number,
    c1: number,
    a2: number,
    c2: number,
    stroke: string,
    width: number
  ) => {
    const [x1, y1] = at(a1, c1);
    const [x2, y2] = at(a2, c2);
    return (
      <line
        stroke={stroke}
        strokeWidth={width}
        x1={px(x1)}
        x2={px(x2)}
        y1={px(y1)}
        y2={px(y2)}
      />
    );
  };
  const q1 = value(stats.q1);
  const q3 = value(stats.q3);
  const median = value(stats.median);
  const [bx, by] = at(Math.min(q1, q3), center - half);
  const length = Math.abs(q3 - q1);
  const [labelX, labelY] = horizontal
    ? [median, center - half - 5]
    : [center + half + 5, median + 4];
  return (
    <g>
      {segment(value(stats.whiskerLow), center, q1, center, color, 1.5)}
      {segment(value(stats.whiskerHigh), center, q3, center, color, 1.5)}
      {segment(
        value(stats.whiskerLow),
        center - cap,
        value(stats.whiskerLow),
        center + cap,
        color,
        1.5
      )}
      {segment(
        value(stats.whiskerHigh),
        center - cap,
        value(stats.whiskerHigh),
        center + cap,
        color,
        1.5
      )}
      <rect
        fill={color}
        fillOpacity={0.22}
        height={px(horizontal ? thickness : length)}
        stroke={color}
        strokeWidth={1.5}
        width={px(horizontal ? length : thickness)}
        x={px(bx)}
        y={px(by)}
      />
      {segment(
        median,
        center - half,
        median,
        center + half,
        CHART_INK.text,
        2.5
      )}
      {stats.outliers.map((outlier, index) => {
        const [x, y] = at(value(outlier), center);
        return (
          <Marker
            fill={color}
            hollow
            // biome-ignore lint/suspicious/noArrayIndexKey: equal outliers repeat, in sorted order
            key={index}
            shape="circle"
            size={8}
            x={x}
            y={y}
          />
        );
      })}
      {showMean ? <MeanMark point={at(value(stats.mean), center)} /> : null}
      {medianLabel ? (
        <text
          fill={CHART_INK.text}
          fontSize={CHART_FONT_SIZE.value}
          textAnchor={horizontal ? "middle" : "start"}
          x={px(labelX)}
          y={px(labelY)}
        >
          {medianLabel}
        </text>
      ) : null}
    </g>
  );
}

function MeanMark({ point }: { point: [number, number] }) {
  return (
    <Marker
      fill={CHART_INK.text}
      shape="diamond"
      size={8}
      x={point[0]}
      y={point[1]}
    />
  );
}

export function BoxPlotChart({
  id,
  groups,
  horizontal,
  showMean,
  legendLabels,
  style,
  fmt,
  label,
  description,
}: {
  /** The `<svg>` id, unique on the page. */
  id: string;
  groups: BoxGroup[];
  horizontal: boolean;
  showMean: boolean;
  /** Names for the mean and outlier symbols in the legend. */
  legendLabels: BoxLegendLabels;
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
}) {
  const low = Math.min(...groups.map((g) => g.stats.min));
  const high = Math.max(...groups.map((g) => g.stats.max));
  const axis = niceTicks(low, high);
  const tick = tickFormat(fmt, axis.decimals);
  const hasOutliers = groups.some((g) => g.stats.outliers.length > 0);
  const keys: LegendKey[] = [];
  const names: string[] = [];
  if (showMean) {
    keys.push({ color: CHART_INK.text, kind: "marker", shape: "diamond" });
    names.push(legendLabels.legendMean);
  }
  if (hasOutliers) {
    keys.push({
      color: paletteColor(colorIndex(style.color), 0),
      kind: "marker",
      shape: "circle",
      hollow: true,
    });
    names.push(legendLabels.legendOutlier);
  }
  const legend =
    names.length > 0
      ? layoutLegend(names, CHART_WIDTH - 2 * CHART_PADDING)
      : null;
  const base: FrameBase = {
    width: CHART_WIDTH,
    title: style.apa ? "" : style.title,
    // The value axis is the y-axis of a vertical plot and the x-axis of a horizontal one.
    xTitle: horizontal ? style.yTitle : style.xTitle,
    yTitle: horizontal ? style.xTitle : style.yTitle,
    legendHeight: legend?.height ?? 0,
    topPadding: style.valueLabels && horizontal ? 18 : 10,
  };
  const geometry = boxGeometry(groups, horizontal, base, axis, tick);
  const { frame } = geometry;
  const color = paletteColor(colorIndex(style.color), 0);
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitles frame={frame} />
      {legend ? (
        <SvgLegend
          keys={keys}
          layout={legend}
          x={CHART_PADDING}
          y={frame.legendTop}
        />
      ) : null}
      <ValueAxis
        apa={style.apa}
        format={tick}
        orient={horizontal ? "bottom" : "left"}
        plot={frame.plot}
        scale={geometry.value}
        ticks={axis.ticks}
      />
      <CategoryAxis
        apa={style.apa}
        axisLine
        labels={geometry.labels}
        orient={horizontal ? "left" : "bottom"}
        plot={frame.plot}
        positions={groups.map((_, i) => geometry.center(i))}
        ticks
      />
      {groups.map((group, index) => (
        <Box
          center={geometry.center(index)}
          color={color}
          horizontal={horizontal}
          key={`${index}-${group.name}`}
          medianLabel={
            style.valueLabels ? valueText(fmt, group.stats.median, 3) : null
          }
          showMean={showMean}
          stats={group.stats}
          thickness={geometry.thickness}
          value={geometry.value}
        />
      ))}
    </ChartSvg>
  );
}
